/**
 * Plan construction.
 *
 * Deliberately deterministic. Nereus advises on a plan and can talk through the
 * trade-offs, but it does NOT emit the plan object — the phase numbers, hold
 * percentages and schedule are computed here from the diver's measured baselines
 * and the documented rules.
 *
 * The reason is safety rather than taste: a hallucinated phase number puts someone
 * into holds their measured max does not support, and free-text parsing of a chat
 * reply is exactly where that would slip through. So the model influences the
 * inputs and the framing; this module owns the numbers.
 */

import { ApneaPlanDto } from '../types/Apnea';
import {
  MaxStatic,
  measuredMaxStatic,
  estimatedMaxStatic,
  recommendedStartPhase,
} from './apneaTables';
import { MAX_TEST_INTERVAL_DAYS } from './apneaRules';

export type ApneaGoal =
  | 'scubaRmv'
  | 'staticDuration'
  | 'depth'
  | 'comfort'
  | 'generalHealth';

export const GOAL_OPTIONS: Array<{ value: ApneaGoal; label: string; detail: string }> = [
  {
    value: 'comfort',
    label: 'Comfort and calm in the water',
    detail: 'The default, and what most of the training actually delivers.',
  },
  {
    value: 'scubaRmv',
    label: 'Lower my scuba air consumption',
    detail: 'Weights breathing mechanics and apnea walks over long static holds.',
  },
  {
    value: 'staticDuration',
    label: 'Longer breath-holds',
    detail: 'Slower and more conservative — the risk sits here, so the ramp is gentle.',
  },
  {
    value: 'depth',
    label: 'Prepare for depth',
    detail: 'Adds equalisation work. Depth itself needs in-water training with an instructor.',
  },
  {
    value: 'generalHealth',
    label: 'General health and HRV',
    detail: 'Lowest risk. Resonance breathing does most of the work here.',
  },
];

export interface PlanIntake {
  goals: ApneaGoal[];
  /** Measured max static in seconds, when they have one. */
  maxStaticSeconds?: number;
  /** A recent BOLT reading, used to estimate a max when none is measured. */
  boltSeconds?: number;
  /** Sessions per week the diver can realistically commit to. */
  daysPerWeek: number;
  currentCertification?: string;
  targetCertification?: string;
  /** True when they have already completed a CO2 base block. */
  co2BaseComplete: boolean;
  /** Anything medical or practical the plan should respect. */
  notes?: string;
}

export interface PlanProposal {
  plan: Omit<ApneaPlanDto, 'userId' | 'sortKey' | 'version' | 'createdAt' | 'supersedes'>;
  /** Why the plan looks like this, in the diver's terms. */
  rationale: string[];
  /** Things the plan deliberately does not include, and why. */
  caveats: string[];
  /** Set when nothing safe can be prescribed yet. */
  blocked?: string;
}

/**
 * The weekly schedule. Built from the documented constraints rather than from a
 * preference: table work capped at 2-3 sessions, never both types in one day, and
 * rest days are prescribed rather than left over.
 */
const buildSchedule = (
  daysPerWeek: number,
  includeO2: boolean,
  goals: ApneaGoal[]
): Record<string, string> => {
  const schedule: Record<string, string> = {};
  const rmvFocused = goals.includes('scubaRmv');

  // Monday is the hardest CO2 day in every variant — it follows the weekend rest.
  schedule.monday = 'co2';
  schedule.tuesday = 'resonance';

  if (daysPerWeek <= 3) {
    // Minimum viable: one table, one breathing, one walk.
    schedule.thursday = rmvFocused ? 'diaphragmatic' : 'resonance';
    schedule.friday = 'apneaWalk';
    schedule.sunday = 'rest';
    return schedule;
  }

  schedule.wednesday = includeO2 ? 'o2' : 'co2';
  schedule.thursday = rmvFocused ? 'diaphragmatic' : 'resonance';
  schedule.friday = 'apneaWalk';

  if (daysPerWeek >= 6) {
    schedule.saturday = goals.includes('depth') ? 'frenzel' : 'diaphragmatic';
  }
  schedule.sunday = 'rest';
  return schedule;
};

export const SESSION_LABELS: Record<string, string> = {
  co2: 'CO₂ table',
  o2: 'O₂ table',
  apneaWalk: 'Apnea walk',
  resonance: 'Resonance breathing (6 bpm)',
  diaphragmatic: '360° diaphragmatic breathing',
  frenzel: 'Frenzel practice',
  rest: 'Rest',
};

/**
 * Build a plan proposal from the intake.
 *
 * Returns `blocked` rather than a plan when there is no safe starting point — a
 * new trainee with no numbers at all needs a BOLT reading first, and inventing a
 * phase for them would defeat the whole gating design.
 */
export const buildPlanProposal = (intake: PlanIntake): PlanProposal => {
  const rationale: string[] = [];
  const caveats: string[] = [];

  const hasMeasured = typeof intake.maxStaticSeconds === 'number' && intake.maxStaticSeconds > 0;
  const hasBolt = typeof intake.boltSeconds === 'number' && intake.boltSeconds > 0;

  if (!hasMeasured && !hasBolt) {
    return {
      plan: emptyPlan(intake),
      rationale: [],
      caveats: [],
      blocked:
        'Take a BOLT reading first — measured on waking, before getting out of bed. '
        + 'Every table is scaled from your own numbers, so there is nothing safe to '
        + 'prescribe without one.',
    };
  }

  const maxStatic: MaxStatic = hasMeasured
    ? measuredMaxStatic(intake.maxStaticSeconds!)
    : estimatedMaxStatic(intake.boltSeconds!);

  if (!hasMeasured) {
    rationale.push(
      `No measured max static yet, so the plan uses a conservative estimate of `
      + `${Math.round(maxStatic.seconds / 60)}:${String(maxStatic.seconds % 60).padStart(2, '0')} `
      + `(BOLT × 4) and holds you at phase 1 until you have a real number.`
    );
    caveats.push(
      'Do a supervised max static when you are rested, and the plan will re-baseline off it.'
    );
  }

  const co2Phase = recommendedStartPhase('co2', maxStatic);
  if (co2Phase === null) {
    return {
      plan: emptyPlan(intake),
      rationale,
      caveats,
      blocked:
        'Your current max static is below what even phase 1 asks for. Build a base with '
        + 'no-contraction tables and breathing work first — hold only to the first '
        + 'contraction, then release. That is real training, not a consolation prize.',
    };
  }

  rationale.push(
    `CO₂ tables start at phase ${co2Phase}, which keeps the constant hold at or under `
    + '75% of your max. CO₂ work is meant to be uncomfortable, not long.'
  );

  // O2 work is gated behind a completed CO2 base, per the program.
  const includeO2 = intake.co2BaseComplete;
  const o2Phase = includeO2 ? recommendedStartPhase('o2', maxStatic) : null;

  if (!includeO2) {
    rationale.push(
      'No O₂ tables yet. CO₂ work is self-limiting because the discomfort stops you; O₂ '
      + 'work feels comfortable while taking you closer to blackout, so it comes after a base.'
    );
  } else if (o2Phase !== null) {
    rationale.push(
      `O₂ tables at phase ${o2Phase}, entered because your measured max has reached that `
      + "phase's reference hold."
    );
  }
  // No else branch: the two gates coincide at 120s. Clearing the CO2 phase-1 ceiling
  // (90s hold <= 75% of max, so max >= 120s) also clears the O2 phase-1 entry bar
  // (max >= 120s), so "has a base but cannot enter O2 phase 1" is unreachable. If
  // either threshold changes, this needs revisiting.

  const daysPerWeek = Math.min(7, Math.max(2, intake.daysPerWeek));
  const schedule = buildSchedule(daysPerWeek, includeO2 && o2Phase !== null, intake.goals);
  const tableDays = Object.values(schedule).filter(v => v === 'co2' || v === 'o2').length;

  rationale.push(
    `${tableDays} table session${tableDays === 1 ? '' : 's'} a week. More than three is `
    + 'counterproductive — tables feel easy and are metabolically expensive, and '
    + 'overtraining shows up as a falling BOLT before it shows up in performance.'
  );

  if (intake.goals.includes('scubaRmv')) {
    rationale.push(
      'Weighted toward breathing mechanics and apnea walks rather than long statics, since '
      + 'the gains you are after come from a full unhurried exhale and staying calm under '
      + 'task load.'
    );
  }
  if (intake.goals.includes('staticDuration')) {
    caveats.push(
      'Chasing duration is where the risk lives. The plan progresses on recovery quality, '
      + 'not on beating your last number — expect that to feel slower than you want.'
    );
  }
  if (intake.goals.includes('depth')) {
    caveats.push(
      'Depth itself needs in-water training with an instructor. This plan can only build the '
      + 'dry foundation and your equalisation.'
    );
  }

  caveats.push('Dry land only. Any in-water breath-hold work needs a rescue-trained buddy.');

  const nextRetest = new Date();
  nextRetest.setDate(nextRetest.getDate() + MAX_TEST_INTERVAL_DAYS);

  return {
    plan: {
      createdBy: 'nereus',
      goals: intake.goals,
      currentCertification: intake.currentCertification ?? null,
      targetCertification: intake.targetCertification ?? null,
      co2Phase,
      o2Phase,
      co2BaseComplete: intake.co2BaseComplete,
      weeklySchedule: schedule,
      baselines: {
        maxStaticSeconds: maxStatic.seconds,
        boltSeconds: intake.boltSeconds ?? null,
        measuredAt: new Date().toISOString(),
        isEstimate: maxStatic.basis === 'estimated',
      },
      nextRetestDue: nextRetest.toISOString(),
      checkInCadenceDays: 14,
      rationale: rationale.join(' '),
    },
    rationale,
    caveats,
  };
};

const emptyPlan = (intake: PlanIntake): PlanProposal['plan'] => ({
  createdBy: 'nereus',
  goals: intake.goals,
  currentCertification: intake.currentCertification ?? null,
  targetCertification: intake.targetCertification ?? null,
  co2Phase: null,
  o2Phase: null,
  co2BaseComplete: intake.co2BaseComplete,
  weeklySchedule: {},
  baselines: {
    maxStaticSeconds: null,
    boltSeconds: intake.boltSeconds ?? null,
    measuredAt: null,
    isEstimate: true,
  },
  nextRetestDue: null,
  checkInCadenceDays: 14,
  rationale: null,
});

/**
 * Prompt for talking the plan through with Nereus.
 *
 * Hands over the plan that was already built and asks for critique, rather than
 * asking the model to produce one. If Nereus disagrees, the diver adjusts the
 * intake and rebuilds — the numbers stay derived from measured baselines either way.
 */
export const planToPrompt = (intake: PlanIntake, proposal: PlanProposal): string => {
  const lines: string[] = ['I am setting up a dry apnea training plan.'];

  const goalLabels = intake.goals
    .map(g => GOAL_OPTIONS.find(o => o.value === g)?.label ?? g)
    .join('; ');
  lines.push(`- What I am after: ${goalLabels || 'not sure yet'}.`);

  if (proposal.plan.baselines.maxStaticSeconds) {
    const s = proposal.plan.baselines.maxStaticSeconds;
    lines.push(
      `- Max static: ${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`
      + `${proposal.plan.baselines.isEstimate ? ' (estimated from BOLT, not measured)' : ''}.`
    );
  }
  if (intake.boltSeconds) lines.push(`- Recent BOLT: ${intake.boltSeconds}s.`);
  lines.push(`- I can train about ${intake.daysPerWeek} days a week.`);
  lines.push(`- CO₂ base complete: ${intake.co2BaseComplete ? 'yes' : 'no'}.`);
  if (intake.notes) lines.push(`- Worth knowing: ${intake.notes}`);

  if (proposal.blocked) {
    lines.push('', `The app will not prescribe tables yet: ${proposal.blocked}`);
    lines.push('Does that sound right, and what should I do in the meantime?');
    return lines.join('\n');
  }

  lines.push('', 'The app has built this:');
  if (proposal.plan.co2Phase) lines.push(`- CO₂ tables at phase ${proposal.plan.co2Phase}.`);
  lines.push(
    proposal.plan.o2Phase
      ? `- O₂ tables at phase ${proposal.plan.o2Phase}.`
      : '- No O₂ tables yet.'
  );
  Object.entries(proposal.plan.weeklySchedule).forEach(([day, kind]) => {
    lines.push(`- ${day}: ${SESSION_LABELS[kind] ?? kind}`);
  });

  lines.push(
    '',
    'Does this look sensible for what I am after? Tell me if anything is too much or too '
    + 'little, and what you would change first. What should my one skill be for the first '
    + 'couple of weeks?'
  );

  return lines.join('\n');
};
